import React, { useEffect, useRef, useState } from 'react'
import styles from './ContactsSection.module.scss'
import SectionTitle from '../SectionTitle/SectionTitle'

const API_PREFIX = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ContactsSection() {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const placemarksRef = useRef(new Map())
  const initedRef = useRef(false)

  useEffect(() => {
    fetch(`${API_PREFIX}/api/sections/get/contacts/list`)
      .then(res => {
        if (!res.ok) throw new Error('Ошибка загрузки контактов')
        return res.json()
      })
      .then(data => {
        setContacts(data.contacts ?? [])
        setLoading(false)
      })
      .catch(e => {
        setError(e.message)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (initedRef.current) return
    if (contacts.length === 0) return
    initedRef.current = true

    if (!window.ymaps) {
      console.error('Yandex Maps API v2.1 не найден. Проверьте тег <script> с apikey.')
      return
    }

    window.ymaps.ready(() => {
      if (!mapRef.current) return

      let map
      try {
        if (!window.ymaps.Map) {
          console.error('ymaps.Map отсутствует. Убедитесь, что загружена API v2.1, а не v3 (ymaps3).')
          return
        }
        map = new window.ymaps.Map(mapRef.current, {
          center: [55.79, 49.12],
          zoom: 13,
          controls: ['zoomControl', 'fullscreenControl'],
        })
        mapInstanceRef.current = map
      } catch (e) {
        console.error('Не удалось создать карту. Вероятно, неверный API key или ограничения по домену.', e)
        return
      }

      const withCoords = contacts
        .map((c, i) => ({ ...c, i }))
        .filter(c => Array.isArray(c.coords) && c.coords.length === 2 && c.coords.every(Number.isFinite))

      const points = withCoords.map(c => c.coords)

      try {
        withCoords.forEach(({ coords, address, time, phone, i }) => {
          const html = `
            <div style="font-family:'Inter',sans-serif;min-width:230px;max-width:300px;">
              <img src="/img/filials/logo.png" alt="Logo" style="width:48px;height:48px;border-radius:12px;display:block;margin:0 auto 12px auto;box-shadow:0 3px 14px #d7ffd242;" />
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:7px;">
                <img src="/img/icons/pin.svg" alt="" style="width:22px;height:22px;" />
                <span style="font-size:18px;font-weight:700;color:#3066F7;">${address}</span>
              </div>
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:3px;">
                <img src="/img/icons/clock.svg" style="width:18px;height:18px;" />
                <span style="font-size:15px;color:#778;">${time || ''}</span>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <img src="/img/icons/phone.svg" style="width:18px;height:18px;" />
                <a href="tel:${phone?.replace(/\s+/g, '')}" style="color:#25D366;text-decoration:none;font-size:15px;font-weight:600;">${phone || ''}</a>
              </div>
            </div>
          `
          const pm = new window.ymaps.Placemark(coords, { balloonContent: html })
          placemarksRef.current.set(i, pm)
          map.geoObjects.add(pm)
        })
      } catch (e) {
        console.error('Ошибка при добавлении плейсмарков. Проверьте ключ и доступность API.', e)
      }

      try {
        if (points.length > 1 && window.ymaps.util?.bounds?.fromPoints) {
          const bounds = window.ymaps.util.bounds.fromPoints(points)
          map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 })
        } else if (points.length === 1) {
          map.setCenter(points[0], 14, { checkZoomRange: true })
        }
      } catch (e) {
        console.warn('Не удалось установить границы карты (не критично).', e)
      }

      const onResize = () => {
        try { map.container.fitToViewport() } catch {}
      }
      window.addEventListener('resize', onResize)
      const ro = new window.ResizeObserver(() => {
        try { map.container.fitToViewport() } catch {}
      })
      ro.observe(mapRef.current)

      return () => {
        window.removeEventListener('resize', onResize)
        ro.disconnect()
        placemarksRef.current.clear()
        try { map.destroy() } catch {}
        mapInstanceRef.current = null
      }
    })
    // eslint-disable-next-line
  }, [contacts])

  const focusPoint = (coords, index) => {
    const map = mapInstanceRef.current
    if (!map || !Array.isArray(coords) || coords.length !== 2) return
    const pm = placemarksRef.current.get(index)
    const nextZoom = Math.max(map.getZoom() || 13, 14)
    const openBalloon = () => {
      try { pm?.balloon?.open() } catch {}
      map.events.group().remove('actionend', openBalloon)
    }
    try {
      map.events.once('actionend', openBalloon)
    } catch {
      map.events.add('actionend', openBalloon)
      setTimeout(() => map.events.remove('actionend', openBalloon), 800)
    }
    try {
      map.setCenter(coords, nextZoom, { checkZoomRange: true, duration: 300 })
    } catch (e) {
      console.error('Не удалось сфокусировать карту. Проверьте валидность ключа API.', e)
    }
  }

  // Нормализатор для телефона (остался как был)
  const normalizePhone = (raw) => {
    let digits = String(raw).replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('8')) digits = '7' + digits.slice(1)
    if (digits.length === 10) digits = '7' + digits
    return digits
  }

  // Нормализатор для Telegram (если прислали просто ник или @ник)
  const normalizeTelegram = (raw) => {
    if (!raw) return '';
    const str = String(raw).trim();
    if (str.startsWith('http')) return str;
    if (str.startsWith('@')) return `https://t.me/${str.slice(1)}`;
    return `https://t.me/${str}`;
  }

  return (
    <section id="contacts" className={styles.contacts}>
      <div className={styles.container}>
        <SectionTitle>Контакты</SectionTitle>
        <div className={styles.subtitle}>
          <p className={styles.link}>
            <span className={styles.orange}>Филиалы</span> в городе <span className={styles.green}>Казань</span>
          </p>
        </div>
        <div className={styles.content}>
          <div className={styles.contactsList}>
            {loading && <div>Загрузка...</div>}
            {error && <div style={{color:'red'}}>Ошибка: {error}</div>}
            {!loading && !error && contacts.map(({ address, time, phone, whatsapp, telegram, coords }, i) => {
              const clickable = Array.isArray(coords) && coords.length === 2
              return (
                <div
                  key={`${address}-${i}`}
                  className={`${styles.contactItem} ${clickable ? styles.clickable : ''}`}
                  onClick={clickable ? () => focusPoint(coords, i) : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : -1}
                  onKeyDown={clickable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      focusPoint(coords, i)
                    }
                  } : undefined}
                >
                  <div className={styles.row}>
                    <img className={styles.icon} src="/img/icons/pin.svg" alt="" />
                    <b>{address}</b>
                  </div>
                  <div className={styles.row}>
                    <img className={styles.icon} src="/img/icons/clock.svg" alt="" />
                    <span>{time}</span>
                  </div>
                  <div className={styles.row} onClick={(e) => e.stopPropagation()}>
                    <img className={styles.icon} src="/img/icons/phone.svg" alt="" />
                    <a href={`tel:${phone?.replace(/\s+/g, '')}`} className={styles.phone}>{phone}</a>
                  </div>

                  {/* Оставил WhatsApp на случай, если он тоже используется */}
                  {whatsapp && (
                    <div className={`${styles.row} ${styles.waRow}`} onClick={(e) => e.stopPropagation()}>
                      <img className={styles.icon} src="/img/icons/whatsapp.svg" alt="" />
                      <a
                        href={`https://wa.me/${normalizePhone(whatsapp)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.waLink}
                      >Написать в WhatsApp</a>
                    </div>
                  )}

                  {/* Новая кнопка для Telegram */}
                  {telegram && (
                    <div className={`${styles.row} ${styles.tgRow}`} onClick={(e) => e.stopPropagation()}>
                      <img className={styles.icon} src="/img/icons/telegram.svg" alt="" />
                      <a
                        href={normalizeTelegram(telegram)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.tgLink}
                      >Написать в Telegram</a>
                    </div>
                  )}

                  {i !== contacts.length - 1 && <hr className={styles.separator} />}
                </div>
              )
            })}
          </div>
          <div className={styles.mapWrapper}>
            <div ref={mapRef} className={styles.map} aria-label="Карта филиалов" />
          </div>
        </div>
      </div>
    </section>
  )
}
